import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  useOcrExtraction,
  useReviewOcrExtraction,
  useRetryOcrExtraction,
  OCR_STATUS_LABEL,
  OCR_STATUS_BADGE_CLASS,
} from "@/api/ocr";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

// Campos con nombre que el extractor heurístico puede proponer (ver
// ocr.service.ts#extractFieldsFromText en el backend) — se muestran como
// inputs editables. El resto de las claves de extractedFields (listas de
// candidatos genéricos como candidateNits/candidateDates) se muestran solo
// como referencia de solo lectura para ayudar a elegir el valor correcto.
const EDITABLE_FIELD_LABEL: Record<string, string> = {
  nit: "NIT",
  razonSocial: "Razón social",
  direccion: "Dirección",
  matricula: "Matrícula mercantil",
  nitProveedor: "NIT del proveedor",
  fecha: "Fecha",
  valor: "Valor",
};

export default function OcrExtractionModal({ extractionId, onClose }: { extractionId: string; onClose: () => void }) {
  const { data: extraction, isLoading } = useOcrExtraction(extractionId);
  const reviewExtraction = useReviewOcrExtraction();
  const retryExtraction = useRetryOcrExtraction();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const [fields, setFields] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (extraction) setFields(extraction.extractedFields ?? {});
  }, [extraction]);

  if (isLoading || !extraction) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
        <div className="card w-full max-w-2xl p-6">
          <p className="text-sm text-gray-400">Cargando extracción OCR...</p>
        </div>
      </div>
    );
  }

  const canReview = hasPermission("documents.approve");
  const editableKeys = Object.keys(EDITABLE_FIELD_LABEL).filter((k) => k in (extraction.extractedFields ?? {}));
  const candidateKeys = Object.keys(extraction.extractedFields ?? {}).filter((k) => !editableKeys.includes(k));

  const handleApprove = async () => {
    try {
      await reviewExtraction.mutateAsync({ id: extraction.id, status: "APPROVED" });
      toast.success("Extracción aprobada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleReject = async () => {
    try {
      await reviewExtraction.mutateAsync({ id: extraction.id, status: "REJECTED" });
      toast.success("Extracción rechazada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleSaveEdit = async () => {
    try {
      await reviewExtraction.mutateAsync({ id: extraction.id, status: "EDITED", extractedFields: fields });
      toast.success("Corrección guardada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleRetry = async () => {
    if (!extraction.document) return;
    try {
      await retryExtraction.mutateAsync({ clientId: extraction.document.clientId, documentId: extraction.document.id });
      toast.success("Reprocesamiento OCR encolado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{extraction.document?.originalName ?? "Documento"}</h2>
            <p className="text-xs text-gray-500">{extraction.document?.category}</p>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-xs ${OCR_STATUS_BADGE_CLASS[extraction.status]}`}>
            {OCR_STATUS_LABEL[extraction.status]}
          </span>
        </div>

        {extraction.status === "FAILED" ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-red-600">El procesamiento OCR falló: {extraction.errorMessage}</p>
            {canReview && (
              <button type="button" className="btn-primary" onClick={handleRetry} disabled={retryExtraction.isPending}>
                {retryExtraction.isPending ? "Reencolando..." : "Reintentar procesamiento"}
              </button>
            )}
          </div>
        ) : (
          <>
            {extraction.confidence && (
              <p className="mt-3 text-xs text-gray-500">Confianza del reconocimiento: {Number(extraction.confidence).toFixed(1)}%</p>
            )}

            {editableKeys.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="text-sm font-medium text-gray-700">Campos extraídos (edite si es necesario)</p>
                {editableKeys.map((key) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500">{EDITABLE_FIELD_LABEL[key]}</label>
                    <input
                      className="input-field mt-1"
                      value={typeof fields[key] === "string" ? (fields[key] as string) : ""}
                      onChange={(e) => setFields((prev) => ({ ...prev, [key]: e.target.value }))}
                      disabled={!canReview}
                    />
                  </div>
                ))}
              </div>
            )}

            {candidateKeys.length > 0 && (
              <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="text-xs font-medium text-gray-500">Otros valores candidatos detectados en el texto</p>
                <div className="mt-1 space-y-1 text-xs text-gray-600">
                  {candidateKeys.map((key) => {
                    const value = extraction.extractedFields[key];
                    const display = Array.isArray(value) ? value.join(", ") : String(value ?? "—");
                    return (
                      <p key={key}>
                        <span className="font-medium">{key}:</span> {display || "—"}
                      </p>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-4">
              <p className="text-xs font-medium text-gray-500">Texto reconocido completo</p>
              <pre className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
                {extraction.rawText || "(sin texto reconocido)"}
              </pre>
            </div>

            {extraction.reviewedBy && (
              <p className="mt-3 text-xs text-gray-400">
                Revisada por {extraction.reviewedBy.fullName} el {extraction.reviewedAt ? new Date(extraction.reviewedAt).toLocaleString("es-CO") : ""}
              </p>
            )}
          </>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cerrar
          </button>
          {canReview && extraction.status !== "FAILED" && (
            <>
              <button type="button" className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50" onClick={handleReject}>
                Rechazar
              </button>
              <button type="button" className="btn-secondary" onClick={handleSaveEdit} disabled={reviewExtraction.isPending}>
                Guardar corrección
              </button>
              <button type="button" className="btn-primary" onClick={handleApprove} disabled={reviewExtraction.isPending}>
                Aprobar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
